import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Card from '../common/Card';
import LoadingSpinner from '../common/LoadingSpinner';
import Icon from '../common/Icon';
import Badge from '../common/Badge';
import { studiesAPI } from '../../services/api';
import { formatDate, formatNumber } from '../../utils/formatters';
import { getCssVar } from '../../utils/cssVar';
import './EvolutionChart.css';

const METRIC_COLOR_VARS = {
  shareOfVoice: '--chart-primary-1',
  estimatedTraffic: '--chart-secondary-vert',
  presenceRate: '--chart-secondary-rose',
  top10: '--chart-secondary-jaune',
  averagePosition: '--chart-secondary-violet',
};

const METRIC_DEFS = [
  { key: 'shareOfVoice', label: 'Part de Voix', unit: '%', formatter: (v) => `${v.toFixed(2)}%` },
  { key: 'estimatedTraffic', label: 'Trafic Estimé', unit: '', formatter: formatNumber },
  { key: 'presenceRate', label: 'Taux de Présence', unit: '%', formatter: (v) => `${v.toFixed(2)}%` },
  { key: 'top10', label: 'Top 10', unit: '', formatter: (v) => v.toString() },
  { key: 'averagePosition', label: 'Position Moyenne', unit: '', formatter: (v) => v ? v.toFixed(1) : '-' },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="evolution-tooltip">
      <p className="evolution-tooltip-label">{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="evolution-tooltip-item">
          <span className="evolution-tooltip-dot" style={{ backgroundColor: entry.color }} />
          <span className="evolution-tooltip-name">{entry.name}</span>
          <span className="evolution-tooltip-value">
            {METRIC_DEFS.find(m => m.key === entry.dataKey)?.formatter(entry.value) || entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const EvolutionChart = ({ studyId, onRelaunch }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMetrics, setSelectedMetrics] = useState(['shareOfVoice', 'estimatedTraffic']);
  const [relaunching, setRelaunching] = useState(false);

  const METRICS = useMemo(
    () => METRIC_DEFS.map((def) => ({ ...def, color: getCssVar(METRIC_COLOR_VARS[def.key]) })),
    []
  );
  const inactiveDotColor = useMemo(() => getCssVar('--color-gray-300'), []);

  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await studiesAPI.getSnapshots(studyId);
        setData(response);
      } catch (err) {
        console.error('Error fetching snapshots:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshots();
  }, [studyId]);

  const chartData = useMemo(() => {
    if (!data?.snapshots) return [];
    
    return data.snapshots
      .filter(s => s.status === 'Completed')
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .map(snapshot => ({
        date: formatDate(snapshot.createdAt),
        studyId: snapshot.studyId,
        snapshotNumber: snapshot.snapshotNumber,
        shareOfVoice: snapshot.shareOfVoice,
        estimatedTraffic: snapshot.estimatedTraffic,
        presenceRate: snapshot.presenceRate,
        top10: snapshot.rank1To3Count + snapshot.rank4To5Count + snapshot.rank6To10Count,
        averagePosition: snapshot.averagePosition,
        rank1To3Count: snapshot.rank1To3Count,
        rank4To5Count: snapshot.rank4To5Count,
        rank6To10Count: snapshot.rank6To10Count,
        rank11To20Count: snapshot.rank11To20Count,
        rank21PlusCount: snapshot.rank21PlusCount,
        presenceCount: snapshot.presenceCount,
        totalKeywords: snapshot.totalKeywords,
      }));
  }, [data]);

  const toggleMetric = useCallback((metricKey) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricKey)) {
        if (prev.length === 1) return prev;
        return prev.filter(k => k !== metricKey);
      }
      return [...prev, metricKey];
    });
  }, []);

  const handleRelaunch = useCallback(async () => {
    if (relaunching) return;
    
    try {
      setRelaunching(true);
      const response = await studiesAPI.relaunch(studyId);
      if (onRelaunch) {
        onRelaunch(response.studyId);
      }
    } catch (err) {
      console.error('Error relaunching study:', err);
      setError(err.message);
    } finally {
      setRelaunching(false);
    }
  }, [studyId, relaunching, onRelaunch]);

  if (loading) {
    return (
      <Card title="Évolution">
        <div className="evolution-loading">
          <LoadingSpinner size="md" text="Chargement de l'historique..." />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Évolution">
        <div className="evolution-error">
          <Icon name="AlertCircle" size={24} color="var(--color-error)" />
          <p>{error}</p>
        </div>
      </Card>
    );
  }

  const hasMultipleSnapshots = chartData.length > 1;

  return (
    <div className="evolution-container">
      <Card 
        title="Évolution des KPIs" 
        action={
          <button 
            className="btn-relaunch" 
            onClick={handleRelaunch}
            disabled={relaunching}
          >
            <Icon name={relaunching ? "Loader" : "RefreshCw"} size={16} />
            {relaunching ? 'Relance en cours...' : 'Relancer l\'étude'}
          </button>
        }
      >
        {hasMultipleSnapshots ? (
          <>
            <div className="evolution-metrics-selector">
              {METRICS.map(metric => (
                <button
                  key={metric.key}
                  className={`metric-toggle ${selectedMetrics.includes(metric.key) ? 'active' : ''}`}
                  onClick={() => toggleMetric(metric.key)}
                  style={{ 
                    '--metric-color': metric.color,
                    borderColor: selectedMetrics.includes(metric.key) ? metric.color : 'transparent'
                  }}
                >
                  <span 
                    className="metric-dot" 
                    style={{ backgroundColor: selectedMetrics.includes(metric.key) ? metric.color : inactiveDotColor }}
                  />
                  {metric.label}
                </button>
              ))}
            </div>

            <div className="evolution-chart">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  {selectedMetrics.includes('estimatedTraffic') && (
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={formatNumber}
                    />
                  )}
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {METRICS.filter(m => selectedMetrics.includes(m.key)).map(metric => (
                    <Line
                      key={metric.key}
                      yAxisId={metric.key === 'estimatedTraffic' ? 'right' : 'left'}
                      type="monotone"
                      dataKey={metric.key}
                      name={metric.label}
                      stroke={metric.color}
                      strokeWidth={2}
                      dot={{ fill: metric.color, strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="evolution-single">
            <Icon name="Info" size={48} color="var(--color-gray-400)" />
            <p>Aucun historique disponible.</p>
            <p className="evolution-single-hint">
              Relancez l'étude pour créer un premier point de comparaison.
            </p>
          </div>
        )}
      </Card>

      <Card title="Historique des analyses">
        <div className="evolution-table-container">
          <table className="evolution-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Statut</th>
                <th>Part de Voix</th>
                <th>Trafic</th>
                <th>Présence</th>
                <th>Top 10</th>
              </tr>
            </thead>
            <tbody>
              {data?.snapshots?.sort((a, b) => b.snapshotNumber - a.snapshotNumber).map((snapshot) => {
                const top10 = snapshot.rank1To3Count + snapshot.rank4To5Count + snapshot.rank6To10Count;
                const isCurrent = snapshot.studyId === studyId;
                
                return (
                  <tr key={snapshot.studyId} className={isCurrent ? 'current' : ''}>
                    <td>
                      <span className="snapshot-number">#{snapshot.snapshotNumber}</span>
                    </td>
                    <td>{formatDate(snapshot.createdAt)}</td>
                    <td>
                      <Badge variant={snapshot.status === 'Completed' ? 'success' : snapshot.status === 'Failed' ? 'danger' : 'warning'}>
                        {snapshot.status === 'Completed' ? 'Terminé' : snapshot.status === 'Failed' ? 'Échoué' : 'En cours'}
                      </Badge>
                    </td>
                    <td>{snapshot.status === 'Completed' ? `${snapshot.shareOfVoice.toFixed(2)}%` : '-'}</td>
                    <td>{snapshot.status === 'Completed' ? formatNumber(snapshot.estimatedTraffic) : '-'}</td>
                    <td>{snapshot.status === 'Completed' ? `${snapshot.presenceRate.toFixed(2)}%` : '-'}</td>
                    <td>{snapshot.status === 'Completed' ? top10 : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default EvolutionChart;
